import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ProductDetail, ExternalId } from '../../../../types/ProductTypes';
import { SectionCard, SectionTitle } from '../../Section.styles';
import { useEditMode } from '../../../../context/EditModeContext';
import { useProductEdit } from '../../../../context/ProductEditContext';
import {
    ExternalIdsTable,
    RemoveExternalIdButton,
    EditInput,
    NewEntryCell,
    AddButton,
    FromCategoryLabel,
    EmptyMessage,
} from './ExternalIdsSection.styles';

interface ExternalIdsSectionProps {
    product: ProductDetail;
}

const ExternalIdsSection: React.FC<ExternalIdsSectionProps> = ({ product }) => {
    const { isInEditMode } = useEditMode();
    const { mergePendingChanges } = useProductEdit();

    const [localExternalIds, setLocalExternalIds] = useState<ExternalId[]>([]);
    const [newSource, setNewSource] = useState('');
    const [newValue, setNewValue] = useState('');
    const tempIdRef = useRef(-1);

    useEffect(() => {
        if (isInEditMode) {
            setLocalExternalIds(product.externalIds || []);
        }
    }, [isInEditMode, product.externalIds]);

    const productCategoryExternalIds = product.category?.externalIds || [];
    const displayExternalIds = isInEditMode ? localExternalIds : product.externalIds || [];

    const isEmpty = useMemo(() => {
        return displayExternalIds.length === 0 && productCategoryExternalIds.length === 0;
    }, [displayExternalIds.length, productCategoryExternalIds.length]);

    const pushExternalIds = useCallback(
        (updated: ExternalId[]) => {
            setLocalExternalIds(updated);
            if (mergePendingChanges) {
                mergePendingChanges({ externalIds: updated });
            }
        },
        [mergePendingChanges]
    );

    const handleRemove = useCallback(
        (id: number) => {
            const updated = localExternalIds.filter((ext) => ext.id !== id);
            pushExternalIds(updated);
        },
        [localExternalIds, pushExternalIds]
    );

    const handleAdd = useCallback(() => {
        const source = newSource.trim();
        const value = newValue.trim();
        if (!source || !value) return;

        if (localExternalIds.some((ext) => ext.source === source && ext.value === value)) {
            setNewSource('');
            setNewValue('');
            return;
        }

        const tempId = tempIdRef.current;
        tempIdRef.current -= 1;
        const newExternalId: ExternalId = {
            id: tempId,
            source,
            value,
            lastUpdatedUtc: new Date().toISOString(),
        };

        const updated = [...localExternalIds, newExternalId];
        pushExternalIds(updated);
        setNewSource('');
        setNewValue('');
    }, [localExternalIds, newSource, newValue, pushExternalIds]);



    return (
        <SectionCard>
            <SectionTitle>External IDs</SectionTitle>

            {isEmpty && !isInEditMode && <EmptyMessage>No external IDs available.</EmptyMessage>}

            <ExternalIdsTable>
                <thead>
                    <tr>
                        <th />
                        <th>Source</th>
                        <th>ID</th>
                    </tr>
                </thead>
                <tbody>
                    {displayExternalIds.map((externalId) => (
                        <tr key={`product-${externalId.id}-${externalId.source}-${externalId.value}`}>
                            <td>
                                {isInEditMode && (
                                    <RemoveExternalIdButton onClick={() => handleRemove(externalId.id)} title="Remove External ID">
                                        🗑️
                                    </RemoveExternalIdButton>
                                )}
                            </td>
                            <td>{externalId.source}</td>
                            <td>{externalId.value}</td>
                        </tr>
                    ))}
                    {productCategoryExternalIds.map((externalId) => (
                        <tr key={`category-${externalId.id}-${externalId.source}-${externalId.value}`}>
                            <td />
                            <td>
                                {externalId.source} <FromCategoryLabel>(from Product Name)</FromCategoryLabel>
                            </td>
                            <td>{externalId.value}</td>
                        </tr>
                    ))}
                </tbody>
                {isInEditMode && (
                    <tfoot>
                        <tr>
                            <td />
                            <td>
                                <EditInput
                                    type="text"
                                    value={newSource}
                                    onChange={(e) => setNewSource(e.target.value)}
                                    placeholder="Source"
                                />
                            </td>
                            <td>
                                <NewEntryCell>
                                    <EditInput
                                        type="text"
                                        value={newValue}
                                        onChange={(e) => setNewValue(e.target.value)}
                                        placeholder="ID"
                                    />
                                    <AddButton onClick={handleAdd} disabled={!newSource.trim() || !newValue.trim()}>
                                        Add
                                    </AddButton>
                                </NewEntryCell>
                            </td>
                        </tr>
                    </tfoot>
                )}
            </ExternalIdsTable>
        </SectionCard>
    );
};

export default ExternalIdsSection;
